import { AlarmHistoryStatusFilter } from 'sigma-mappings';
import attentionMapService from '@/domain/services/generalPanel/attentionMap';
import { filterableClassifications, filterableOrigins } from './attentionMapFilter';

/**
 * Quantos alarmes a lista pede por vez.
 *
 * A lista mostra o topo e não rola: trinta cobre a altura de uma TV de parede com
 * folga e ainda é uma resposta pequena o bastante para caber no ciclo de
 * atualização sem competir com as páginas do mapa.
 */
export const LATEST_ALARMS_PAGE_SIZE = 30;

/**
 * **Alarme em Aberto** traduzido para o filtro do endpoint: alarme atuado,
 * reconhecido ou não, mais o que atuou, normalizou e ninguém reconheceu.
 *
 * O que fica de fora é o único par que não está em aberto — normalizado **e**
 * reconhecido, que é alarme encerrado. Ver *Alarme em Aberto* no CONTEXT.md.
 */
export const OPEN_ALARM_STATUS = [
  AlarmHistoryStatusFilter.ACTED_NOT_RECOGNIZED,
  AlarmHistoryStatusFilter.ACTED_RECOGNIZED,
  AlarmHistoryStatusFilter.NOT_ACTED_NOT_RECOGNIZED,
];

/**
 * Monta o corpo da chamada a partir do estado da tela.
 *
 * Duas regras não são negociáveis e por isso vivem aqui, e não no componente:
 * a categoria nunca inclui Alarme de Rede, e o status é sempre o Alarme em
 * Aberto.
 *
 * Sem filtro escolhido, os dois eixos vão explícitos em vez de vazios. Vazio
 * significa *sem restrição* para o endpoint, e sem restrição ele entregaria
 * qualquer valor que exista na base — inclusive um que a tela não saiba rotular.
 * Enumerar custa nada e fecha essa porta.
 *
 * @param {Object} criteria Escopo, recorte do mapa e filtros da Barra de Controles.
 * @returns {Object} Parâmetros de `attentionMapService.getAlarms`.
 */
export function buildRequest(criteria) {
  const {
    scope = {},
    installationsIds,
    assetsIds,
    classifications = [],
    origins = [],
    pageSize = LATEST_ALARMS_PAGE_SIZE,
  } = criteria || {};

  return {
    companiesIds: scope.companiesIds || [],
    regionalsIds: scope.regionalsIds || [],
    installationsIds: installationsIds || scope.installationsIds || [],
    assetsIds: assetsIds || [],
    categories: origins.length ? origins : filterableOrigins,
    classifications: classifications.length ? classifications : filterableClassifications,
    alarmStatus: OPEN_ALARM_STATUS,
    page: 1,
    pageSize,
  };
}

/**
 * Um item da lista a partir de um alarme da resposta.
 *
 * `key` é o id do alarme, e não o do ativo: a lista tem uma linha por alarme, e
 * o mesmo Ativo aparece tantas vezes quantos alarmes tiver.
 */
function buildItem(alarm) {
  return {
    key: alarm.alarmId,
    name: alarm.name,
    assetId: alarm.assetId,
    assetName: alarm.assetName,
    installationId: alarm.installationId,
    installation: alarm.installation,
    companyId: alarm.companyId,
    company: alarm.company,
    regional: alarm.regional,
    classification: alarm.classification,
    category: alarm.category,
    acted: !!alarm.acted,
    recognized: !!alarm.recognized,
    openedAt: alarm.openedAt,
  };
}

/**
 * Últimos Alarmes e Avisos do recorte, do mais recente para o mais antigo.
 *
 * A ordem vem do servidor e não é refeita aqui: ela é a razão de existir do
 * endpoint, e reordenar no front só teria efeito dentro da página recebida — o
 * que daria uma lista ordenada por engano, certa nas trinta linhas visíveis e
 * errada em relação ao que ficou de fora.
 *
 * @param {Object} criteria Ver `buildRequest`.
 * @returns {Promise<Object>} `{ items, total }`.
 */
export default function loadLatestAlarms(criteria) {
  return attentionMapService
    .getAlarms(buildRequest(criteria))
    .then((response) => ({
      items: (response.data || []).map(buildItem),
      total: response.total || 0,
    }));
}
